import { TextField, Autocomplete, Box, Chip } from "@mui/material";

import { useCountryListing } from "../../api/queries/network/useCountryListing";

type NationalityOption = {
  label: string;
  value: number;
  flagSrc: string;
};

type NationalityAutocompleteProps = {
  multiple?: boolean;
  limitTags?: number;
  value?: number | number[] | null;
  onChange?: (value: number | number[] | null) => void;
  error?: boolean;
};

const NationalityAutocomplete = ({
  multiple = false,
  limitTags = 2,
  value,
  onChange,
  error,
  ...props
}: NationalityAutocompleteProps) => {
  const { countries, isCountriesDataLoading } = useCountryListing();
  const nationalities = countries.map(
    ({ value, flagSrc, nationality }: { label: string; value: string; flagSrc: string; nationality: string }) => ({
      value,
      label: nationality,
      flagSrc,
    }),
  );

  return (
    <Autocomplete
      {...props}
      multiple={multiple}
      limitTags={limitTags}
      disablePortal
      disabled={isCountriesDataLoading}
      options={nationalities}
      renderInput={(params) => <TextField {...params} error={error} />}
      value={
        value
          ? nationalities.filter((item: NationalityOption) =>
              multiple ? Array.isArray(value) && value.includes(item.value) : item.value === value,
            )
          : null
      }
      onChange={(_, newValue) => {
        if (!newValue) {
          onChange?.(null);
        } else if (Array.isArray(newValue)) {
          onChange?.(newValue.map((item) => item.value));
        } else {
          onChange?.(newValue.value);
        }
      }}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderOption={(props, option: { label: string; value: string; flagSrc: string }) => (
        <Box component="li" {...props} sx={styles.option}>
          <Box component="img" src={option.flagSrc} alt={option.label} sx={styles.flag} />
          {option.label}
        </Box>
      )}
      renderTags={(values, getTagProps) =>
        values.map((option, index) => (
          <Chip
            sx={styles.autocompleteChip}
            label={
              <Box component="li" sx={styles.autocompleteChipLabel}>
                <Box component="img" src={option.flagSrc} alt={option.label} sx={styles.flag} />
                {option.label}
              </Box>
            }
            {...getTagProps({ index })}
          />
        ))
      }
      sx={styles.autocomplete}
    />
  );
};

export default NationalityAutocomplete;

const styles = {
  autocomplete: {
    "& .MuiAutocomplete-input": {
      padding: "0 !important",
    },
  },
  flag: {
    width: "32px",
    height: "24px",
    borderRadius: "4px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "24px",
  },
  autocompleteChipLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "var(--Gray-700, #344054)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  autocompleteChip: {
    borderRadius: "6px",
    margin: "0 !important",
    border: "1px solid var(--Gray-300, #D0D5DD)",
    background: "var(--Base-White, #FFF)",
    gap: "4px",
    padding: "2px 5px",

    "& .MuiChip-label": {
      padding: 0,
    },
    "& .MuiChip-deleteIcon": {
      margin: 0,
    },
  },
};
